// domain/model/Authorization.js
import { isCapPeriod, isPeriodicCap, isResetBehavior, isStatedUnit } from '../constants/authorization';
import { createApprovedLimit } from './ApprovedLimit';
import { createEffectivePeriod, endsBeforeStart, unreadableBounds } from './EffectivePeriod';

/**
 * Behavior of the Authorization aggregate. The shape and the `@domainObject` tag live in
 * `Authorization.types` — one concept, so one place declares what it is.
 *
 * @see ./Authorization.types
 */

/** Validation failures, as codes. The domain does not know about i18n; the UI maps these. */
export const AUTHORIZATION_ERRORS = {
  FUNDING_SOURCE_REQUIRED: 'fundingSourceRequired',
  PERIOD_START_REQUIRED: 'periodStartRequired',
  PERIOD_END_REQUIRED: 'periodEndRequired',
  PERIOD_END_BEFORE_START: 'periodEndBeforeStart',
  UNREADABLE_PERIOD_DATE: 'unreadablePeriodDate',
  LIMIT_REQUIRED: 'limitRequired',
  LIMIT_ID_REQUIRED: 'limitIdRequired',
  LIMIT_DUPLICATE_ID: 'limitDuplicateId',
  LIMIT_SERVICE_REQUIRED: 'limitServiceRequired',
  LIMIT_DUPLICATE_SERVICE: 'limitDuplicateService',
  LIMIT_CAP_REQUIRED: 'limitCapRequired',
  LIMIT_DUPLICATE_CAP_PERIOD: 'limitDuplicateCapPeriod',
  CAP_FIGURE_REQUIRED: 'capFigureRequired',
  CAP_FIGURE_NOT_POSITIVE: 'capFigureNotPositive',
  CAP_STATED_UNIT_INVALID: 'capStatedUnitInvalid',
  CAP_PERIOD_INVALID: 'capPeriodInvalid',
  CAP_RESET_BEHAVIOR_INVALID: 'capResetBehaviorInvalid',
};

/**
 * A non-list reads as empty. Every raw list on this aggregate arrives from storage as well as from
 * a form, and `validateAuthorization` reads it before `createAuthorization` normalizes it: one of
 * the two throwing where the other tolerates would make the order they run in load-bearing.
 *
 * @param {*} value
 * @returns {Array<*>}
 */
function asList(value) {
  return Array.isArray(value) ? value : [];
} // asList()

/**
 * Builds an Authorization, filling defaults. Accepts a partial so it serves both "new, blank" and
 * "hydrate from storage".
 *
 * Period bounds are accepted FLAT, which is what the document, the form draft and the projector all
 * speak, and assembled into the `period` value object here.
 *
 * NORMALIZATION IS LOSSY, and deliberately asymmetric with `validateAuthorization`: a bound this
 * cannot read becomes an OPEN bound, which for an end date reads as "never expires". So the order
 * matters — validate the raw input, then build.
 *
 * @param {Partial<import('./Authorization.types').Authorization> &
 *   { periodStart?: *, periodEnd?: * }} [attrs]
 * @returns {import('./Authorization.types').Authorization}
 */
export function createAuthorization(attrs = {}) {
  return {
    authorizationId: attrs.authorizationId ?? null,
    clientId: attrs.clientId ?? null,
    clinicId: attrs.clinicId ?? null,
    fundingSourceId: attrs.fundingSourceId ?? null,
    // Absent, empty and blank are the same statement here — no number on file — so a cleared or
    // space-only text field normalizes to null rather than becoming a number made of nothing. A
    // non-string is not a number on file either: status reads anything but null as issued.
    authorizationNumber: typeof attrs.authorizationNumber === 'string'
      ? attrs.authorizationNumber.trim() || null
      : null,
    period: createEffectivePeriod(attrs.periodStart, attrs.periodEnd),
    // A non-object limit is dropped, but one missing only its id or its services SURVIVES: dropping
    // it would quietly reduce what the funder approved, where keeping it leaves something the biller
    // can see and validation refuses to re-save.
    limits: asList(attrs.limits)
      .filter((limit) => limit !== null && typeof limit === 'object')
      .map(createApprovedLimit),
    isDeleted: attrs.isDeleted === true,
  };
} // createAuthorization()

/**
 * Every cap fact that has to be true on its own terms.
 *
 * Figures that are not finite numbers return `CAP_FIGURE_REQUIRED`; finite figures at or below
 * zero return `CAP_FIGURE_NOT_POSITIVE`. Numeric strings are not coerced.
 *
 * `resetBehavior` is checked only on a PERIODIC cap: `createCap` drops one supplied on a total, so
 * reporting it would name a field the form does not show for that period.
 *
 * @param {Partial<import('./Cap').Cap>} cap - raw, pre-normalization
 * @returns {string[]} AUTHORIZATION_ERRORS codes, empty when the cap is valid
 */
function capErrors(cap) {
  const errors = [];

  if (!Number.isFinite(cap?.figure)) {
    errors.push(AUTHORIZATION_ERRORS.CAP_FIGURE_REQUIRED);
  } else if (cap.figure <= 0) {
    errors.push(AUTHORIZATION_ERRORS.CAP_FIGURE_NOT_POSITIVE);
  }
  if (!isStatedUnit(cap?.statedUnit)) {
    errors.push(AUTHORIZATION_ERRORS.CAP_STATED_UNIT_INVALID);
  }
  if (!isCapPeriod(cap?.period)) {
    errors.push(AUTHORIZATION_ERRORS.CAP_PERIOD_INVALID);
  }
  if (isPeriodicCap(cap?.period)
    && cap?.resetBehavior !== null
    && cap?.resetBehavior !== undefined
    && !isResetBehavior(cap.resetBehavior)) {
    errors.push(AUTHORIZATION_ERRORS.CAP_RESET_BEHAVIOR_INVALID);
  }

  return errors;
} // capErrors()

/**
 * Every limit fact that has to be true, including the two set rules a single cap cannot see.
 *
 * ONE CAP PER PERIOD, since nothing would arbitrate between two weekly ceilings. Caps whose period
 * is unrecognized are skipped — `capErrors` has already reported them.
 *
 * A DUPLICATE SERVICE WITHIN ONE LIMIT is refused, unlike the same service on two DIFFERENT limits,
 * which is legitimate when the funder scopes it by setting. Inside one pool it draws on the amount
 * once either way.
 *
 * @param {Partial<import('./ApprovedLimit').ApprovedLimit>} limit - raw, pre-normalization
 * @returns {Array<{ code: string, capIndex: number|null }>}
 */
function limitErrors(limit) {
  const errors = [];
  const services = asList(limit?.services);
  const caps = asList(limit?.caps);

  if (!limit?.limitId) {
    errors.push({ code: AUTHORIZATION_ERRORS.LIMIT_ID_REQUIRED, capIndex: null });
  }

  const serviceIds = services.map((service) => service?.treatmentServiceId).filter(Boolean);

  if (serviceIds.length === 0) {
    errors.push({ code: AUTHORIZATION_ERRORS.LIMIT_SERVICE_REQUIRED, capIndex: null });
  }
  if (new Set(serviceIds).size !== serviceIds.length) {
    errors.push({ code: AUTHORIZATION_ERRORS.LIMIT_DUPLICATE_SERVICE, capIndex: null });
  }

  if (caps.length === 0) {
    errors.push({ code: AUTHORIZATION_ERRORS.LIMIT_CAP_REQUIRED, capIndex: null });
  }

  const statedPeriods = caps.map((cap) => cap?.period).filter(isCapPeriod);

  if (new Set(statedPeriods).size !== statedPeriods.length) {
    errors.push({ code: AUTHORIZATION_ERRORS.LIMIT_DUPLICATE_CAP_PERIOD, capIndex: null });
  }

  caps.forEach((cap, capIndex) => {
    capErrors(cap).forEach((code) => errors.push({ code, capIndex }));
  });

  return errors;
} // limitErrors()

/**
 * Validates an authorization on its own terms.
 *
 * Takes RAW ATTRIBUTES, not a built aggregate: once the factory has run, an unreadable bound and a
 * deliberately open one are both null and no longer tellable apart.
 *
 * RETURNS LOCATED ERRORS, `{ code, limitIndex, capIndex }`, where `validateCoverage` returns bare
 * codes. Forced by the shape rather than chosen — a coverage is flat, so a code names its own field,
 * while a letter with four limits and six caps would leave the biller no way to know which row it is
 * about. Header failures carry a null `limitIndex`.
 *
 * BOTH POSITIONS, NOT THE LIMIT'S ID. A limit has one, but a limit being entered has not got it yet:
 * infrastructure mints the id, and until the draft is staged the editor holds a null. Locating by
 * position is what lets a row show its own errors while it is still being typed.
 *
 * @param {{ fundingSourceId?: string|null, periodStart?: *, periodEnd?: *,
 *   limits?: Array<Partial<import('./ApprovedLimit').ApprovedLimit>> }} attrs - raw
 * @returns {Array<{ code: string, limitIndex: number|null, capIndex: number|null }>} empty when valid
 */
export function validateAuthorization(attrs) {
  const errors = [];
  const header = (code) => errors.push({ code, limitIndex: null, capIndex: null });

  if (attrs.fundingSourceId === null || attrs.fundingSourceId === undefined) {
    header(AUTHORIZATION_ERRORS.FUNDING_SOURCE_REQUIRED);
  }

  // Asked of the RAW bounds, before the period is built. Normalization turns anything `toDateStr`
  // refuses into an OPEN bound, so an unreadable end date would silently turn an approval that
  // expires into one that never does, and the gate would keep clearing holds on it forever.
  if (unreadableBounds(attrs.periodStart, attrs.periodEnd)) {
    header(AUTHORIZATION_ERRORS.UNREADABLE_PERIOD_DATE);
  }

  const period = createEffectivePeriod(attrs.periodStart, attrs.periodEnd);

  // A period is happy open at either end; a letter is not. Which bounds are REQUIRED is the
  // holder's rule, so it stays here.
  if (period.start === null) {
    header(AUTHORIZATION_ERRORS.PERIOD_START_REQUIRED);
  }
  if (period.end === null) {
    header(AUTHORIZATION_ERRORS.PERIOD_END_REQUIRED);
  }
  if (endsBeforeStart(period)) {
    header(AUTHORIZATION_ERRORS.PERIOD_END_BEFORE_START);
  }

  const limits = asList(attrs.limits);

  if (limits.length === 0) {
    header(AUTHORIZATION_ERRORS.LIMIT_REQUIRED);
  }

  // Reported at the header because a collision is a fault of the SET, and neither of the pair is
  // the wrong one. It cannot come from the UI, which mints an id per row; the realistic source is a
  // migration or repair script, where a mis-attributed consumption entry would be unrecoverable.
  const limitIds = limits.map((entry) => entry?.limitId).filter(Boolean);

  if (new Set(limitIds).size !== limitIds.length) {
    header(AUTHORIZATION_ERRORS.LIMIT_DUPLICATE_ID);
  }

  limits.forEach((limit, limitIndex) => {
    limitErrors(limit).forEach(({ code, capIndex }) => {
      errors.push({ code, limitIndex, capIndex });
    });
  });

  return errors;
} // validateAuthorization()

/**
 * Which services appear on more than one limit. A WARNING, NOT AN INVARIANT: a funder scoping one
 * service two ways by setting states it twice, so refusing would make a real approval unrecordable.
 *
 * @param {{ limits?: Array<Partial<import('./ApprovedLimit').ApprovedLimit>> }} attrs
 * @returns {string[]} treatment service ids, each once
 */
export function servicesOnSeveralLimits(attrs) {
  const seen = new Set();
  const repeated = new Set();

  asList(attrs.limits).forEach((limit) => {
    const ids = new Set(
      asList(limit?.services).map((service) => service?.treatmentServiceId).filter(Boolean),
    );

    ids.forEach((id) => (seen.has(id) ? repeated.add(id) : seen.add(id)));
  });

  return [...repeated];
} // servicesOnSeveralLimits()
